/**
 * MarkerCluster - Wrapper para leaflet.markercluster con react-leaflet
 * V03 - Clustering de Marcadores
 *
 * Agrupa marcadores cercanos para mejorar rendimiento y visualización
 * cuando hay muchas infraestructuras (50-500+ puntos por municipio).
 */

import React, { useEffect, useRef, useMemo, useState, createContext, useContext } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

import { createClusterIcon } from './ClusterIcon';
import './cluster.css';

/**
 * Opciones de configuración del MarkerClusterGroup
 */
export interface MarkerClusterOptions {
  /** Radio máximo en píxeles para agrupar marcadores (default: 50) */
  maxClusterRadius?: number;
  /** Expandir en "araña" en zoom máximo (default: true) */
  spiderfyOnMaxZoom?: boolean;
  /** Mostrar área de cobertura al hover (default: true) */
  showCoverageOnHover?: boolean;
  /** Zoom a límites del cluster al hacer clic (default: true) */
  zoomToBoundsOnClick?: boolean;
  /** Eliminar clusters fuera del viewport (default: true) */
  removeOutsideVisibleBounds?: boolean;
  /** Animar agrupación/desagrupación (default: true) */
  animate?: boolean;
  /** Animar al añadir marcadores (default: false, mejor rendimiento) */
  animateAddingMarkers?: boolean;
  /** Distancia de spiderfy en píxeles (default: 40) */
  spiderfyDistanceMultiplier?: number;
  /** Usar función de icono personalizada PTEL (default: true) */
  usePtelIcons?: boolean;
}

/**
 * Props del componente MarkerCluster
 */
export interface MarkerClusterProps {
  /** Marcadores a agrupar (Marker components) */
  children?: React.ReactNode;
  /** Opciones de clustering */
  options?: MarkerClusterOptions;
  /** Callback cuando se hace clic en un cluster */
  onClusterClick?: (cluster: L.MarkerCluster, event: L.LeafletMouseEvent) => void;
  /** Callback cuando el mouse entra en un cluster */
  onClusterMouseOver?: (cluster: L.MarkerCluster, event: L.LeafletMouseEvent) => void;
  /** Callback cuando el mouse sale de un cluster */
  onClusterMouseOut?: (cluster: L.MarkerCluster, event: L.LeafletMouseEvent) => void;
  /** Mostrar número de elementos (default: true) */
  showCount?: boolean;
}

/**
 * Opciones por defecto optimizadas para PTEL
 */
const DEFAULT_OPTIONS: Required<MarkerClusterOptions> = {
  maxClusterRadius: 50,
  spiderfyOnMaxZoom: true,
  showCoverageOnHover: true,
  zoomToBoundsOnClick: true,
  removeOutsideVisibleBounds: true,
  animate: true,
  animateAddingMarkers: false, // Mejor rendimiento
  spiderfyDistanceMultiplier: 1.5,
  usePtelIcons: true,
};

/**
 * Contexto para pasar el cluster group a los hijos
 */
const MarkerClusterContext = createContext<L.MarkerClusterGroup | null>(null);

/**
 * Hook para acceder al MarkerClusterGroup desde componentes hijos
 */
export function useMarkerCluster(): L.MarkerClusterGroup | null {
  return useContext(MarkerClusterContext);
}

/**
 * Componente MarkerCluster
 * 
 * Wrapper que agrupa marcadores Leaflet usando leaflet.markercluster.
 * Los marcadores hijos se añaden automáticamente al cluster group.
 * 
 * @example
 * ```tsx
 * <MarkerCluster options={{ maxClusterRadius: 60 }}>
 *   {infraestructuras.map((infra) => (
 *     <Marker key={infra.id} position={[infra.lat, infra.lng]}>
 *       <Popup>{infra.nombre}</Popup>
 *     </Marker>
 *   ))}
 * </MarkerCluster>
 * ```
 */
export function MarkerCluster({
  children,
  options = {},
  onClusterClick,
  onClusterMouseOver,
  onClusterMouseOut,
}: MarkerClusterProps): React.ReactElement | null {
  const map = useMap();
  const clusterGroupRef = useRef<L.MarkerClusterGroup | null>(null);
  // Estado sincronizado para exponer el cluster group al contexto fuera de efectos
  const [clusterGroup, setClusterGroup] = useState<L.MarkerClusterGroup | null>(null);

  // Memoizar opciones
  const mergedOptions = useMemo(() => {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    
    const clusterOptions: L.MarkerClusterGroupOptions = {
      maxClusterRadius: opts.maxClusterRadius,
      spiderfyOnMaxZoom: opts.spiderfyOnMaxZoom,
      showCoverageOnHover: opts.showCoverageOnHover,
      zoomToBoundsOnClick: opts.zoomToBoundsOnClick,
      removeOutsideVisibleBounds: opts.removeOutsideVisibleBounds,
      animate: opts.animate,
      animateAddingMarkers: opts.animateAddingMarkers,
      spiderfyDistanceMultiplier: opts.spiderfyDistanceMultiplier,
    };

    if (opts.usePtelIcons) {
      clusterOptions.iconCreateFunction = createClusterIcon;
    }

    return clusterOptions;
  }, [options]);

  // Crear cluster group
  useEffect(() => {
    if (!clusterGroupRef.current) {
      const group = L.markerClusterGroup(mergedOptions);
      clusterGroupRef.current = group;
      map.addLayer(group);
      // eslint-disable-next-line react-hooks/set-state-in-effect -- inicialización única; sincroniza contexto con la instancia Leaflet recién creada
      setClusterGroup(group);
    }

    return () => {
      if (clusterGroupRef.current) {
        map.removeLayer(clusterGroupRef.current);
        clusterGroupRef.current = null;
         
        setClusterGroup(null);
      }
    };
  }, [map, mergedOptions]);

  // Event handlers
  useEffect(() => {
    const clusterGroup = clusterGroupRef.current;
    if (!clusterGroup) return;

    const handleClusterClick = (e: L.LeafletMouseEvent) => {
      const cluster = e.propagatedFrom || e.layer;
      if (cluster && 'getChildCount' in cluster) {
        onClusterClick?.(cluster as L.MarkerCluster, e);
      }
    };

    const handleClusterMouseOver = (e: L.LeafletMouseEvent) => {
      const cluster = e.propagatedFrom || e.layer;
      if (cluster && 'getChildCount' in cluster) {
        onClusterMouseOver?.(cluster as L.MarkerCluster, e);
      }
    };

    const handleClusterMouseOut = (e: L.LeafletMouseEvent) => {
      const cluster = e.propagatedFrom || e.layer;
      if (cluster && 'getChildCount' in cluster) {
        onClusterMouseOut?.(cluster as L.MarkerCluster, e);
      }
    };

    if (onClusterClick) {
      clusterGroup.on('clusterclick', handleClusterClick);
    }
    if (onClusterMouseOver) {
      clusterGroup.on('clustermouseover', handleClusterMouseOver);
    }
    if (onClusterMouseOut) {
      clusterGroup.on('clustermouseout', handleClusterMouseOut);
    }

    return () => {
      clusterGroup.off('clusterclick', handleClusterClick);
      clusterGroup.off('clustermouseover', handleClusterMouseOver);
      clusterGroup.off('clustermouseout', handleClusterMouseOut);
    };
  }, [onClusterClick, onClusterMouseOver, onClusterMouseOut]);

  return (
    <MarkerClusterContext.Provider value={clusterGroup}>
      {children}
    </MarkerClusterContext.Provider>
  );
}

/**
 * Componente helper para añadir un marcador al cluster group
 * Debe usarse dentro de MarkerCluster
 */
export interface ClusteredMarkerProps {
  /** Posición del marcador [lat, lng] */
  position: L.LatLngExpression;
  /** Icono personalizado (opcional) */
  icon?: L.Icon | L.DivIcon;
  /** Contenido del popup (opcional) */
  popupContent?: string | HTMLElement;
  /** Contenido del tooltip (opcional) */
  tooltipContent?: string;
  /** Datos adicionales asociados al marcador */
  data?: Record<string, unknown>;
  /** Callback al hacer clic en el marcador */
  onClick?: (marker: L.Marker, event: L.LeafletMouseEvent) => void;
}

/**
 * Marcador que se añade automáticamente al cluster group padre
 */
export function ClusteredMarker({
  position,
  icon,
  popupContent,
  tooltipContent,
  data,
  onClick,
}: ClusteredMarkerProps): null {
  const clusterGroup = useMarkerCluster();
  const markerRef = useRef<L.Marker | null>(null);

  useEffect(() => {
    if (!clusterGroup) return;

    // Crear marcador
    const marker = L.marker(position, { icon });

    // Añadir popup si existe
    if (popupContent) {
      marker.bindPopup(popupContent);
    }

    // Añadir tooltip si existe
    if (tooltipContent) {
      marker.bindTooltip(tooltipContent);
    }

    // Guardar datos adicionales
    if (data) {
      (marker as L.Marker & { data?: Record<string, unknown> }).data = data;
    }

    // Event handler
    if (onClick) {
      marker.on('click', (e) => onClick(marker, e));
    }

    // Añadir al cluster group
    clusterGroup.addLayer(marker);
    markerRef.current = marker;

    return () => {
      if (markerRef.current) {
        clusterGroup.removeLayer(markerRef.current);
        markerRef.current = null;
      }
    };
  }, [clusterGroup, position, icon, popupContent, tooltipContent, data, onClick]);

  // Este componente no renderiza nada directamente
  return null;
}

export default MarkerCluster;
